import React, { useRef } from 'react';
import { Button } from '@fiverr-private/button';
import { Stack, Container } from '@fiverr-private/layout_components';
import { Typography } from '@fiverr-private/typography';
import { Box } from '@fiverr-private/theme';
import { ElementType } from '@fiverr-private/bianka_js';
import { useEnteredView } from '@fiverr-private/hooks';
import DragScrollCarousel from '../DragScrollCarousel';
import { sendBiankaImpression, sendBiankaClick, sendMixpanelEvent } from '../../shared/biEvents';
import { EVENT_NAMES } from '../../shared/biEvents/eventNames';
import { enrichUrlWithParams } from '../../shared/urlEnrichment';
import { BRANDED_CATEGORY_LINKS, CATEGORY_LINKS_DEFAULT, CATEGORY_LINKS_V16 } from './constants';
import { buttonClass } from './styles.ve.css';

const SearchCategoriesLinks: React.FC<{ isV16?: boolean; isBrandPage?: boolean }> = ({
  isV16 = false,
  isBrandPage = false,
}) => {
  const observableRef = useRef<HTMLDivElement>(null);
  const categoryLinks = isV16 ? CATEGORY_LINKS_V16 : CATEGORY_LINKS_DEFAULT;
  const categoryLinksToRender = isBrandPage ? BRANDED_CATEGORY_LINKS : categoryLinks;

  useEnteredView(observableRef, {
    threshold: 1,
    onEntered: () => {
      sendBiankaImpression({
        eventName: EVENT_NAMES.BIANKA.POPULAR_SERVICES_TAGS_ARE_SHOWN,
        componentName: 'popular_services_tags',
        componentText: 'Popular Services Tags',
        isV16,
      });
    },
  });

  const handleCategoryClick = (category: (typeof categoryLinksToRender)[0], index: number) => {
    sendBiankaClick({
      eventName: EVENT_NAMES.BIANKA.CLICKED_ON_POPULAR_SERVICES_TAG,
      elementName: 'popular_services_tag',
      elementType: ElementType.BUTTON,
      elementText: category.title,
      position: index + 1,
      isV16,
    });

    sendMixpanelEvent({
      eventName: EVENT_NAMES.MIXPANEL.LOHP_CLICKED_POPULAR_SERVICES_TAGS,
      clickedElement: category.title,
      clickedElementOrder: index + 1,
      isV16,
    });
  };

  const renderCategoryButton = (category: (typeof categoryLinksToRender)[0], index: number) => (
    <Button
      key={category.href}
      href={enrichUrlWithParams(category.href, 'header_lohp')}
      target="_blank"
      variant="outline"
      className={buttonClass}
      onDragStart={(e) => e.preventDefault()}
      onClick={() => handleCategoryClick(category, index)}
    >
      {category.title}
      {category.badge && (
        <Container
          marginLeft="2"
          paddingX="2"
          paddingY="1"
          backgroundColor="black"
          borderRadius="3xl"
          display="inlineFlex"
          alignItems="center"
          justifyContent="center"
          minWidth="69px"
          height="24px"
        >
          <Typography color="white" size="b_xxs" fontWeight="bold">
            {category.badge.toUpperCase()}
          </Typography>
        </Container>
      )}
    </Button>
  );

  // TODO: check if div is not breaking lazyout
  return (
    <Container ref={observableRef}>
      <Box display={{ default: 'block', md: 'none' }}>
        <DragScrollCarousel gap="3" itemWidth="auto" showFadeOverlay={true}>
          {categoryLinksToRender.map((category, index) => (
            <Container padding="0.5" key={category.href}>
              {renderCategoryButton(category, index)}
            </Container>
          ))}
        </DragScrollCarousel>
      </Box>

      <Box display={{ default: 'none', md: 'block' }}>
        <Stack direction="row" justifyContent="center" alignItems="center" gap="4" wrap="wrap">
          {categoryLinksToRender.map((category, index) => renderCategoryButton(category, index))}
        </Stack>
      </Box>
    </Container>
  );
};

export default SearchCategoriesLinks;
